import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { sb } from "@/lib/sb";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { refreshGoogleReviews, saveGooglePlaceId } from "@/lib/google-reviews.functions";

export const Route = createFileRoute("/_authenticated/dashboard/settings")({
  component: SettingsPage,
});


type Settings = {
  analytics_report_email: string | null;
  security_report_email: string | null;
  speed_report_email: string | null;
  form_submission_email: string | null;
  brand_label: string | null;
};

function SettingsPage() {
  const [s, setS] = useState<Settings>({
    analytics_report_email: "",
    security_report_email: "",
    speed_report_email: "",
    form_submission_email: "",
    brand_label: "",
  });
  const [newPw, setNewPw] = useState("");
  const [placeId, setPlaceId] = useState("");
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [reviewCount, setReviewCount] = useState<number>(0);
  const [busy, setBusy] = useState(false);
  const savePlace = useServerFn(saveGooglePlaceId);
  const refreshNow = useServerFn(refreshGoogleReviews);

  useEffect(() => {
    sb.from("app_settings")
      .select("*")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }: any) => {
        if (!data) return;
        setS(data);
        setPlaceId(data.google_place_id ?? "");
        setLastUpdated(data.google_reviews_updated_at ?? null);
        setReviewCount(Array.isArray(data.google_reviews) ? data.google_reviews.length : 0);
      });
  }, []);


  const save = async () => {
    const { error } = await sb.from("app_settings").update(s).eq("id", 1);
    if (error) toast.error(error.message);
    else toast.success("Saved");
  };

  const changePassword = async () => {
    if (newPw.length < 8) return toast.error("Password must be at least 8 characters.");
    const { error } = await supabase.auth.updateUser({ password: newPw });
    if (error) toast.error(error.message);
    else {
      toast.success("Password updated");
      setNewPw("");
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="font-display text-2xl font-bold text-ink">Settings</h1>

      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h2 className="font-display font-semibold">Email recipients</h2>
        {(
          [
            ["analytics_report_email", "Analytics Report Email"],
            ["security_report_email", "Security Report Email"],
            ["speed_report_email", "Speed Report Email"],
            ["form_submission_email", "Form Submission Email"],
            ["brand_label", "Dashboard brand label"],
          ] as const
        ).map(([k, label]) => (
          <div key={k}>
            <label className="text-xs text-muted-foreground">{label}</label>
            <input
              value={(s as any)[k] ?? ""}
              onChange={(e) => setS({ ...s, [k]: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border"
            />
          </div>
        ))}
        <button
          onClick={save}
          className="px-4 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground font-semibold text-sm"
        >
          Save
        </button>
        <p className="text-xs text-muted-foreground">
          Email delivery is enabled once a transactional email function is wired up. Saved
          addresses are read by that function when it runs.
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h2 className="font-display font-semibold">Google Reviews</h2>
        <p className="text-xs text-muted-foreground">
          Paste your Google Business Profile <strong>Place ID</strong>. Reviews shown on
          the homepage refresh from Google automatically (every 6 hours). Google returns the
          5 most recent reviews per business.
        </p>
        <label className="text-xs text-muted-foreground">Google Place ID</label>
        <input
          value={placeId}
          onChange={(e) => setPlaceId(e.target.value)}
          placeholder="e.g. ChIJN1t_tDeuEmsRUsoyG83frY4"
          className="w-full px-3 py-2 rounded-lg bg-background border border-border font-mono text-sm"
        />
        <div className="flex flex-wrap gap-2">
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await savePlace({ data: { placeId } });
                const fresh = await refreshNow();
                setLastUpdated(fresh.updated_at);
                setReviewCount(fresh.reviews.length);
                toast.success(`Saved & pulled ${fresh.reviews.length} reviews from Google`);
              } catch (e: any) {
                toast.error(e?.message || "Failed");
              } finally {
                setBusy(false);
              }
            }}
            className="px-4 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground font-semibold text-sm disabled:opacity-50"
          >
            Save & pull reviews
          </button>
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const fresh = await refreshNow();
                setLastUpdated(fresh.updated_at);
                setReviewCount(fresh.reviews.length);
                toast.success(`Pulled ${fresh.reviews.length} reviews`);
              } catch (e: any) {
                toast.error(e?.message || "Failed");
              } finally {
                setBusy(false);
              }
            }}
            className="px-4 py-2 rounded-lg bg-background border border-border text-sm disabled:opacity-50"
          >
            Refresh now
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          {reviewCount} cached review{reviewCount === 1 ? "" : "s"}
          {lastUpdated ? ` · last pulled ${new Date(lastUpdated).toLocaleString()}` : " · not pulled yet"}
        </p>
        <p className="text-xs text-muted-foreground">
          Find your Place ID:{" "}
          <a
            href="https://developers.google.com/maps/documentation/places/web-service/place-id"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Google Place ID Finder
          </a>
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h2 className="font-display font-semibold">Change My Password</h2>

        <input
          type="password"
          value={newPw}
          onChange={(e) => setNewPw(e.target.value)}
          placeholder="New password (8+ chars)"
          className="w-full px-3 py-2 rounded-lg bg-background border border-border"
        />
        <button
          onClick={changePassword}
          className="px-4 py-2 rounded-lg bg-background border border-border text-sm"
        >
          Update Password
        </button>
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h2 className="font-display font-semibold">Admin access</h2>
        <p className="text-sm text-muted-foreground">
          Admin access is now managed through the dedicated Admin area so the dashboard matches the Blaine layout.
        </p>
      </div>
    </div>
  );
}
